import { store } from '../store';
import { featureFlags } from '../config/featureFlags';
import { getClientAppGateHeaders } from '../utils/clientAppGate';

const API_BASE_URL = featureFlags.general.apiUrl;

interface ApiResponse<T = any> {
  status: string;
  code: number;
  message: string;
  data: T;
  errors?: string[];
}

interface Game {
  id: number;
  name: string;
  provider: string;
  category: string;
  rtp: string;
  volatility: string;
  minBet?: number;
  maxBet?: number;
  image?: string;
  enabled: boolean;
}

interface GameProvider {
  id: number;
  name: string;
  enabled: boolean;
  apiKey: string;
  apiUrl: string;
  gameCount: number;
  category: string;
}

interface GameSession {
  gameId: number;
  sessionId: string;
  launchUrl: string;
  expiresAt: string;
}

interface SportsEvent {
  id: number;
  sport: string;
  league: string;
  homeTeam: string;
  awayTeam: string;
  time: string;
  date: string;
  markets?: any[];
}

interface Bet {
  id: number;
  type: string;
  eventName: string;
  marketType: string;
  selection: string;
  odds: number;
  stake: number;
  potentialPayout?: number;
  status: string;
  payout?: number;
  createdAt: string;
}

interface SavedPrediction {
  id?: number;
  gameWeek: number;
  homeTeam: string;
  awayTeam: string;
  date: string;
  time: string;
  venue: string;
  homeTeamStats: {
    position: number;
    wins: number;
    draws: number;
    losses: number;
    goalsFor: number;
    goalsAgainst: number;
    form: string[];
  };
  awayTeamStats: {
    position: number;
    wins: number;
    draws: number;
    losses: number;
    goalsFor: number;
    goalsAgainst: number;
    form: string[];
  };
  prediction?: {
    homeWin: number;
    draw: number;
    awayWin: number;
    mostLikely: 'home' | 'draw' | 'away';
    confidence: number;
    predictedScore: string;
  };
  createdAt?: string;
}

class IGamingService {
  private getAuthHeaders(): Record<string, string> {
    const state = store.getState().auth;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Context': 'retail',
      ...getClientAppGateHeaders(),
    };

    if (state.token && state.type === 'JWT') {
      headers['Authorization'] = `Bearer ${state.token}`;
    }

    return headers;
  }

  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const headers = this.getAuthHeaders();
      const state = store.getState().auth;

      const requestOptions: RequestInit = {
        ...options,
        headers: {
          ...headers,
          ...(options.headers || {}),
        },
        credentials: state.type === 'SESSION' ? 'include' : 'omit',
      };

      const response = await fetch(`${API_BASE_URL}${endpoint}`, requestOptions);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<T> = await response.json();
      
      // Handle StandardApiResponse structure from backend
      // Backend returns status: "success" or "error", and code: 200 for success
      if (result.status === 'success' || result.status === 'Successful' || result.code === 200) {
        return result;
      } else {
        throw new Error(result.message || result.errors?.[0] || 'Request failed');
      }
    } catch (error) {
      console.error('iGaming API request failed:', error);
      throw error;
    }
  }

  // Casino Games
  async getCasinoGames(params?: {
    page?: number;
    size?: number;
    providerId?: number;
    category?: string;
    q?: string;
  }): Promise<{ content: Game[]; totalElements: number; totalPages: number; currentPage: number; size: number }> {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.providerId) queryParams.append('providerId', params.providerId.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.q) queryParams.append('q', params.q);

    const response = await this.request<{ content: Game[]; totalElements: number; totalPages: number; currentPage: number; size: number }>(
      `/api/igaming/casino/games?${queryParams.toString()}`
    );

    return response.data;
  }

  async launchGame(gameId: number): Promise<GameSession> {
    const response = await this.request<GameSession>(
      `/api/igaming/casino/games/${gameId}/launch`,
      { method: 'POST' }
    );

    return response.data;
  }

  // Sports Betting
  async getSportsEvents(params?: {
    page?: number;
    size?: number;
    sport?: string;
    league?: string;
  }): Promise<{ content: SportsEvent[]; totalElements: number; totalPages: number; currentPage: number; size: number }> {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sport) queryParams.append('sport', params.sport);
    if (params?.league) queryParams.append('league', params.league);

    const response = await this.request<{ content: SportsEvent[]; totalElements: number; totalPages: number; currentPage: number; size: number }>(
      `/api/igaming/sports/events?${queryParams.toString()}`
    );

    return response.data;
  }

  async placeBet(betData: {
    type: string;
    eventId: number;
    eventName: string;
    marketType: string;
    selection: string;
    odds: number;
    stake: number;
  }): Promise<{ betId: number; status: string; message: string }> {
    const response = await this.request<{ betId: number; status: string; message: string }>(
      '/api/igaming/sports/bets',
      {
        method: 'POST',
        body: JSON.stringify(betData),
      }
    );

    return response.data;
  }

  async getBettingHistory(params?: {
    page?: number;
    size?: number;
  }): Promise<{ content: Bet[]; totalElements: number; totalPages: number; currentPage: number; size: number }> {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const response = await this.request<{ content: Bet[]; totalElements: number; totalPages: number; currentPage: number; size: number }>(
      `/api/igaming/bets/history?${queryParams.toString()}`
    );

    return response.data;
  }

  // Live Casino
  async getLiveGames(params?: {
    page?: number;
    size?: number;
  }): Promise<{ content: any[]; totalElements: number; totalPages: number; currentPage: number; size: number }> {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const response = await this.request<{ content: any[]; totalElements: number; totalPages: number; currentPage: number; size: number }>(
      `/api/igaming/live/games?${queryParams.toString()}`
    );

    return response.data;
  }

  // Saved Predictions
  async savePrediction(prediction: SavedPrediction): Promise<SavedPrediction> {
    const response = await this.request<SavedPrediction>(
      '/api/igaming/predictions/save',
      {
        method: 'POST',
        body: JSON.stringify(prediction),
      }
    );

    return response.data;
  }

  async getSavedPredictions(): Promise<SavedPrediction[]> {
    const response = await this.request<SavedPrediction[]>(
      '/api/igaming/predictions'
    );

    return response.data;
  }

  async deleteSavedPrediction(id: number): Promise<void> {
    await this.request<void>(
      `/api/igaming/predictions/${id}`,
      {
        method: 'DELETE',
      }
    );
  }

  // iGaming Account
  async getIGamingBalance(): Promise<{ balance: number; currency: string }> {
    const response = await this.request<{ balance: number; currency: string }>(
      '/api/igaming-account/balance'
    );

    return response.data;
  }

  async getIGamingAccount(): Promise<any> {
    const response = await this.request<any>(
      '/api/igaming-account'
    );

    return response.data;
  }

  // Game betting operations
  async placeGameBet(amount: number): Promise<{ newBalance: number; totalWagered: number }> {
    const params = new URLSearchParams();
    params.append('amount', amount.toString());

    const response = await this.request<{ newBalance: number; totalWagered: number }>(
      `/api/igaming-account/bet?${params.toString()}`,
      { method: 'POST' }
    );

    return response.data;
  }

  async creditWinnings(amount: number): Promise<{ newBalance: number; totalWon: number }> {
    const params = new URLSearchParams();
    params.append('amount', amount.toString());

    const response = await this.request<{ newBalance: number; totalWon: number }>(
      `/api/igaming-account/win?${params.toString()}`,
      { method: 'POST' }
    );

    return response.data;
  }

  async recordLoss(amount: number): Promise<{ newBalance: number; totalLost: number }> {
    const params = new URLSearchParams();
    params.append('amount', amount.toString());

    const response = await this.request<{ newBalance: number; totalLost: number }>(
      `/api/igaming-account/loss?${params.toString()}`,
      { method: 'POST' }
    );

    return response.data;
  }
}

export const igamingService = new IGamingService();
export type { Game, GameProvider, GameSession, SportsEvent, Bet, SavedPrediction };

